import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCheckoutPreview } from "./api";
import { useCart } from "./context/CartContext";
import ShopNavBar from "./components/ShopNavBar";
import EmptyState from "./components/EmptyState";

export default function CartPreview() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem } = useCart();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (items.length === 0) {
      setPreview(null);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      getCheckoutPreview(shopId, {
        items: items.map((i) => ({
          productId: i.productId,
          productVariantId: i.productVariantId,
          quantity: i.quantity,
        })),
      })
        .then((res) => {
          setPreview(res.data);
          setError(null);
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(handle);
  }, [items, shopId]);

  if (items.length === 0) {
    return (
      <div className="min-h-screen">
        <ShopNavBar title="Your Cart" />
        <EmptyState
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 4h2l2.4 12.2a2 2 0 002 1.8h8.4a2 2 0 002-1.7L21 8H6"
                stroke="#9CA3AF"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="10" cy="21" r="1.4" fill="#9CA3AF" />
              <circle cx="18" cy="21" r="1.4" fill="#9CA3AF" />
            </svg>
          }
          title="Your cart is empty"
          subtitle="Add something you like from the shop to get started."
          action={
            <button
              onClick={() => navigate(`/shop/${shopId}`)}
              className="w-full otaGradientButton text-white font-semibold rounded-xl py-3 active:scale-[0.98] transition-transform"
            >
              Browse products
            </button>
          }
        />
      </div>
    );
  }

  const pricing = preview?.pricing;

  return (
    <div className="min-h-screen pb-32">
      <ShopNavBar title="Your Cart" showProfile={false} />

      <div className="px-4 pt-4 flex flex-col gap-3">
        {items.map((item) => {
          const line = preview?.items?.find(
            (i) =>
              i.productId === item.productId &&
              i.productVariantId === item.productVariantId,
          );
          return (
            <div
              key={`${item.productId}::${item.productVariantId || ""}`}
              className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 cardShadow"
            >
              <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                {item.image && (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium otaDark truncate">
                  {item.title}
                </p>
                {item.variantName && (
                  <p className="text-xs otaLight">{item.variantName}</p>
                )}
                <p className="text-sm font-bold otaFont mt-0.5">
                  ₹{(item.price * item.quantity).toFixed(0)}
                </p>
                {line && !line.inStock && (
                  <p className="text-xs text-red-600 mt-0.5">
                    Only {line.availableStock} left in stock
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-1.5 py-1 shrink-0">
                <button
                  onClick={() =>
                    updateQuantity(
                      item.productId,
                      item.productVariantId,
                      item.quantity - 1,
                    )
                  }
                  className="w-6 h-6 font-semibold otaDark active:bg-gray-100 rounded transition-colors"
                >
                  −
                </button>
                <span className="text-sm font-semibold w-4 text-center">
                  {item.quantity}
                </span>
                <button
                  onClick={() =>
                    updateQuantity(
                      item.productId,
                      item.productVariantId,
                      item.quantity + 1,
                    )
                  }
                  className="w-6 h-6 font-semibold otaDark active:bg-gray-100 rounded transition-colors"
                >
                  +
                </button>
              </div>
              <button
                onClick={() =>
                  removeItem(item.productId, item.productVariantId)
                }
                className="text-red-500 text-xs font-medium shrink-0"
              >
                Remove
              </button>
            </div>
          );
        })}

        {error && <p className="text-sm text-red-600 text-center">{error}</p>}

        {pricing && (
          <div className="rounded-xl border border-gray-100 p-4 mt-2 flex flex-col gap-1.5 bg-gray-50/60">
            <div className="flex justify-between text-sm otaLight">
              <span>Subtotal</span>
              <span>₹{pricing.itemsSubtotal.toFixed(0)}</span>
            </div>
            <div className="flex justify-between text-sm otaLight">
              <span>Shipping</span>
              <span>₹{pricing.shippingFee.toFixed(0)}</span>
            </div>
            <div className="flex justify-between text-sm otaLight">
              <span>Tax</span>
              <span>₹{pricing.taxAmount.toFixed(0)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold otaDark pt-2 border-t border-gray-200 mt-1">
              <span>Total</span>
              <span>₹{pricing.baseSubtotal.toFixed(0)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] p-4 bg-white border-t border-gray-100">
        <button
          onClick={() => navigate(`/shop/${shopId}/checkout`)}
          disabled={loading || !preview?.valid}
          className="w-full otaGradientButton text-white font-semibold rounded-xl py-3.5 disabled:opacity-50 active:scale-[0.98] transition-transform disabled:active:scale-100"
        >
          {loading
            ? "Updating…"
            : preview && !preview.valid
              ? "Some items are out of stock"
              : "Proceed to Checkout"}
        </button>
      </div>
    </div>
  );
}
